import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackLaunchClick, trackLaunchVisit } from "../lib/launchTracking";

/**
 * First-party marketing analytics.
 * - Captures a $pageview via PostHog if it's already loaded on the page
 *   (the existing app already embeds PostHog once in public/index.html).
 * - EXPLICITLY DISABLES SESSION RECORDING on the marketing site while any
 *   MarketingLayout is mounted, and restores it on unmount so the
 *   authenticated /app/* experience is unaffected.
 */
export function useMarketingAnalytics() {
  const location = useLocation();

  // Toggle session recording on mount/unmount of the marketing layout.
  useEffect(() => {
    const ph = typeof window !== "undefined" ? window.posthog : null;
    if (ph && typeof ph.stopSessionRecording === "function") {
      try {
        ph.stopSessionRecording();
      } catch (_) {}
    }
    return () => {
      const p = typeof window !== "undefined" ? window.posthog : null;
      if (p && typeof p.startSessionRecording === "function") {
        try {
          p.startSessionRecording();
        } catch (_) {}
      }
    };
  }, []);

  // Fire a pageview on every route change inside the marketing tree.
  useEffect(() => {
    trackLaunchVisit();
    const ph = typeof window !== "undefined" ? window.posthog : null;
    if (ph && typeof ph.capture === "function") {
      try {
        ph.capture("$pageview", {
          $current_url: window.location.href,
          surface: "marketing",
          path: location.pathname,
        });
      } catch (_) {}
    }
  }, [location.pathname]);
}

function ph() {
  return typeof window !== "undefined" ? window.posthog : null;
}

function safeCapture(event, props) {
  const client = ph();
  if (!client || typeof client.capture !== "function") return;
  try {
    client.capture(event, { surface: "marketing", ...(props || {}) });
  } catch (_) {}
}

/**
 * Generic CTA click. Also auto-emits dedicated high-value events so we can
 * still analyse "Get started" and "Product tour" clicks in isolation without
 * touching every button call site.
 */
export function trackCta(name, extra = {}) {
  safeCapture("marketing_cta_click", { name, ...extra });
  const n = String(name || "");
  if (n === "app_store_download") {
    trackLaunchClick("app_store", extra.source);
  }
  if (
    n.endsWith("_getstarted") ||
    n.includes("getstarted") ||
    n === "hero_primary" ||
    n === "final_cta" ||
    n.startsWith("pricing_")
  ) {
    safeCapture("marketing_get_started_click", { source: n, ...extra });
    trackLaunchClick("get_started", extra.plan ? `${n}_${extra.plan}` : n);
  }
  if (n.includes("tour")) {
    safeCapture("marketing_product_tour_click", { source: n, ...extra });
  }
}

export function trackDemoPosterOpen(extra = {}) {
  safeCapture("marketing_demo_poster_open", extra);
}
export function trackDemoPlay(extra = {}) {
  safeCapture("marketing_demo_play", extra);
}
export function trackDemoComplete(extra = {}) {
  safeCapture("marketing_demo_complete", extra);
}
export function trackDemoProgress(pct, extra = {}) {
  safeCapture("marketing_demo_progress", { pct, ...extra });
}
export function trackProductTourClick(source, extra = {}) {
  safeCapture("marketing_product_tour_click", { source, ...extra });
}
export function trackGetStartedClick(source, extra = {}) {
  safeCapture("marketing_get_started_click", { source, ...extra });
}
